// Organization view (agent org chart) styles (WEB-8004). Shell surface —
// composed by workspaceStyles.ts at the sections' original positions.
export const organizationView = `
/* ===== Organization view ===== */
.ws-org-layout {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 360px;
  min-height: 0;
  overflow: hidden;
}
.ws-org-canvas {
  overflow: auto;
  background: var(--ws-bg);
  position: relative;
}
.ws-org-canvas-inner {
  padding: 48px 40px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  position: relative;
  min-height: 100%;
}
.ws-org-grid-bg {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0);
  background-size: 20px 20px;
  opacity: 0.6;
  pointer-events: none;
}
.ws-org-level { display: flex; justify-content: center; position: relative; z-index: 1; }
.ws-org-level-row { display: flex; gap: 18px; justify-content: center; margin-top: 20px; flex-wrap: wrap; align-items: stretch; }
.ws-org-tree-node { display: flex; flex-direction: column; align-items: center; }
.ws-org-connector {
  width: 1.5px; height: 34px;
  background: var(--ws-line);
  margin: 14px 0;
  position: relative; z-index: 0;
}
.ws-org-branch-wrap {
  width: 100%; max-width: 720px;
  height: 20px;
  position: relative;
  margin-top: 14px;
}
.ws-org-branch-line {
  position: absolute;
  left: 12%; right: 12%; top: 0;
  height: 1.5px;
  background: var(--ws-line);
}

/* Org cards */
.ws-org-card {
  background: white;
  border: 1.5px solid var(--ws-line);
  border-radius: 10px;
  padding: 14px 16px;
  min-width: 200px;
  max-width: 240px;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(.4,0,.2,1);
  position: relative;
  box-shadow: 0 1px 2px rgba(19,17,14,0.04);
}
.ws-org-card[draggable="true"] {}
.ws-org-card:hover {
  border-color: var(--ws-ink-4);
  box-shadow: 0 4px 16px rgba(19,17,14,0.06), 0 1px 2px rgba(19,17,14,0.04);
  transform: translateY(-1px);
}
.ws-org-card.selected {
  border: 1.5px solid #2F7A4E;
  box-shadow: 0 0 0 4px rgba(47,122,78,0.08), 0 4px 16px rgba(19,17,14,0.06);
}
.ws-org-card-human {
  background: linear-gradient(180deg, #f6f9fb, #edf1f5);
  border-color: rgba(184,147,90,0.35);
  cursor: default;
}
.ws-org-card-human:hover { transform: none; }
.ws-org-card-lead {
  background: linear-gradient(180deg, #FFFFFF, #f6f9fb);
}
.ws-org-card-head { display: flex; gap: 10px; align-items: center; }
.ws-org-card-sw {
  width: 28px; height: 28px; border-radius: 7px;
  display: grid; place-items: center;
  color: white;
  font-family: var(--ws-serif);
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
}
.ws-org-card-title-col { min-width: 0; flex: 1; }
.ws-org-card-name {
  font-family: var(--ws-serif);
  font-weight: 600;
  font-size: 15.5px;
  color: var(--ws-ink);
  line-height: 1.15;
  letter-spacing: -0.005em;
}
.ws-org-card-role { font-size: 11.5px; color: var(--ws-ink-4); margin-top: 1px; }
.ws-org-card-meta {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px dashed var(--ws-line);
  font-size: 11px;
  color: var(--ws-ink-4);
}
.ws-org-status { display: inline-flex; align-items: center; gap: 5px; font-weight: 500; }
.ws-org-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--ws-ink-4);
}
.ws-org-status.active { color: #2F7A4E; }
.ws-org-status.active .ws-org-dot {
  background: #2F7A4E;
  box-shadow: 0 0 0 2px rgba(47,122,78,0.18);
}
.ws-org-pill-dark {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--ws-ink);
  color: #e8f9f8;
}
/* Org-wide agents get a loud, unmistakable badge — a filled accent pill with a
   people glyph — so they read as "shared across the whole org" at a glance. */
.ws-org-pill-org {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 7px 2px 6px;
  border-radius: 999px;
  background: var(--ws-accent);
  color: #e8f9f8;
}
.ws-org-pill-org svg { opacity: 0.9; }
/* Personal agents stay quiet so the org badge is the thing that pops. */
.ws-org-pill-soft {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--ws-soft);
  color: var(--ws-ink-4);
}
/* Agent-page header badge — same accent language as the org-chart pill, sized
   up so an org-wide agent is unmistakable at the top of its own page. */
.ws-agent-org-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  vertical-align: middle;
  margin-left: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 9px 3px 8px;
  border-radius: 999px;
  background: var(--ws-accent);
  color: #e8f9f8;
}

/* Org tabs */
.ws-org-tabs {
  display: flex; align-items: center; gap: 4px;
  justify-content: center;
  border-bottom: 1px solid var(--ws-line);
  padding: 16px 40px 0;
}
.ws-org-tabs button {
  padding: 10px 14px 10px 12px;
  border: 0; background: transparent;
  color: var(--ws-ink-4); font-family: var(--ws-sans);
  font-size: 13px; font-weight: 500;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer;
}
.ws-org-tabs button.active { color: var(--ws-ink); border-bottom-color: var(--ws-ink); font-weight: 600; }
.ws-org-tab-count {
  font-size: 11px; background: var(--ws-soft); padding: 0 6px;
  border-radius: 9px; color: var(--ws-ink-4); font-weight: 500;
}

/* Team list */
.ws-team-list {
  display: flex; flex-direction: column; gap: 0;
  width: 100%; max-width: 520px;
  background: var(--ws-panel); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
}
.ws-team-list-header {
  display: grid; grid-template-columns: 1fr auto;
  align-items: center; gap: 8px;
  padding: 10px 16px;
  font-size: 10.5px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--ws-ink-4);
  background: var(--ws-soft); border-bottom: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md) var(--ws-r-md) 0 0;
}
.ws-team-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; cursor: pointer;
  transition: background 0.1s;
}
.ws-team-row + .ws-team-row { border-top: 1px solid var(--ws-line); }
.ws-team-row:hover { background: var(--ws-soft); }
.ws-team-row.selected { background: rgba(19,17,14,0.04); }
.ws-team-row-info {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 1px;
}
.ws-team-row-owner { font-size: 12px; color: var(--ws-ink-3); }
.ws-team-row-role { font-size: 11.5px; color: var(--ws-ink-4); }

/* Inspector */
.ws-org-inspector {
  border-left: 1px solid var(--ws-line);
  background: white;
  padding: 28px 24px;
  overflow: auto;
  display: flex; flex-direction: column; gap: 18px;
}
.ws-oi-head { display: flex; gap: 12px; align-items: center; }
.ws-oi-sw { width: 36px; height: 36px; font-size: 15px; border-radius: 9px; }
.ws-oi-name {
  font-family: var(--ws-serif);
  font-size: 20px; font-weight: 600;
  color: var(--ws-ink); line-height: 1.1;
}
.ws-oi-role { font-size: 12px; color: var(--ws-ink-4); margin-top: 2px; }
.ws-oi-section { display: flex; flex-direction: column; gap: 8px; }
.ws-oi-section-head {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--ws-ink-4);
  display: flex; align-items: center; gap: 6px;
}
.ws-oi-section-body {
  font-size: 13.5px; line-height: 1.5; color: var(--ws-ink-3);
}
.ws-oi-vision {
  font-family: var(--ws-serif);
  font-size: 15px; font-style: italic;
  color: var(--ws-ink-3);
}
.ws-oi-list {
  margin: 0; padding-left: 0;
  list-style: none;
  font-size: 13px; line-height: 1.45;
  color: var(--ws-ink-3);
  display: flex; flex-direction: column; gap: 6px;
}
.ws-oi-list li {
  padding-left: 18px;
  position: relative;
}
.ws-oi-list li::before {
  content: ''; position: absolute;
  left: 0; top: 7px;
  width: 10px; height: 1.5px;
  background: var(--ws-ink-4);
}
.ws-oi-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.ws-oi-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px; font-weight: 500;
  background: var(--ws-bg);
  color: var(--ws-ink-3);
  border: 1px solid var(--ws-line);
}
.ws-oi-open-btn {
  display: flex; align-items: center; justify-content: center; gap: 7px;
  width: 100%;
  padding: 9px 14px;
  border-radius: 999px;
  border: 0;
  background: var(--ws-ink);
  color: #e8f9f8;
  font-size: 13px; font-weight: 500;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.12s;
}
.ws-oi-open-btn:hover { background: #244856; }

/* Agent color vars */
.ai-ws { --ws-a-human: #B8935A; }
`;

export const organizationOrgChartButton = `
/* ===== Org chart "+" button ===== */
.ws-org-add-btn {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 8px; width: 100%; padding: 8px 12px;
  border: 1px dashed var(--ws-line-2); background: transparent;
  color: var(--ws-ink-3); border-radius: var(--ws-r-md);
  font-size: 12px; cursor: pointer; transition: background 0.12s, border-color 0.12s;
}
.ws-org-add-btn:hover { background: var(--ws-hover); border-color: var(--ws-ink-4); color: var(--ws-ink); }
.ws-org-card.ws-org-drop-hint {
  border-style: dashed;
  border-color: rgba(47,122,78,0.35);
}
.ws-org-card.ws-org-drop-target {
  border-color: #2F7A4E;
  border-style: solid;
  background: rgba(47,122,78,0.04);
  box-shadow: 0 0 0 2px rgba(47,122,78,0.12);
}
.ws-org-card.ws-org-dragging {
  opacity: 0.35; pointer-events: none;
}
.ws-org-card[draggable="true"] { cursor: grab; }
.ws-org-card[draggable="true"]:active { cursor: grabbing; }
.ws-org-card.ws-org-card-add {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  border: 1.5px dashed var(--ws-line-2); background: transparent;
  color: var(--ws-ink-3); cursor: pointer;
  align-self: stretch;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.ws-org-card.ws-org-card-add:hover { background: var(--ws-hover); border-color: var(--ws-ink-4); color: var(--ws-ink); }
.ws-org-card.ws-org-card-add span { font-size: 12px; }
`;
